import {DataObjectId} from '../data-object'
import {
  BoardPermissions,
  getDefaultBoardPermissions,
  isDisabledBoardProp
} from './board-permissions'
import {
  ColumnPermissions,
  getDefaultColumnPermissions,
  TaskPermissionsType
} from './column-permissions'
import {
  getDefaultProjectPermissions,
  ProjectPermissions
} from './project-permissions'
import {
  getDefaultTaskPermissions,
  isDisabledTaskProp,
  TaskPermissions
} from './task-permissions'

export type ProjectChildPermission =
  | {type: 'task'; permissions: TaskPermissions}
  | {type: 'column'; permissions: ColumnPermissions}
  | {type: 'board'; permissions: BoardPermissions}

export type PermissionObject =
  | {type: 'task'; objectId: DataObjectId; permissions: TaskPermissions}
  | {type: 'column'; objectId: DataObjectId; permissions: ColumnPermissions}
  | {type: 'board'; objectId: DataObjectId; permissions: BoardPermissions}
  | {type: 'project'; objectId: DataObjectId; permissions: ProjectPermissions}

export interface PermissionsById {
  [objectId: string]: PermissionObject
}

export const permissionChildToObject = (
  objectId: DataObjectId,
  child: ProjectChildPermission | undefined
): PermissionObject | undefined => {
  if (child?.type === 'task') {
    return {type: child.type, objectId, permissions: child.permissions}
  } else if (child?.type === 'board') {
    return {type: child.type, objectId, permissions: child.permissions}
  } else if (child?.type === 'column') {
    return {type: child.type, objectId, permissions: child.permissions}
  } else {
    return undefined
  }
}

export const permissionObjectToChild = (
  obj: PermissionObject
): ProjectChildPermission | undefined => {
  if (obj.type === 'task') {
    return {type: obj.type, permissions: obj.permissions}
  } else if (obj.type === 'board') {
    return {type: obj.type, permissions: obj.permissions}
  } else if (obj.type === 'column') {
    return {type: obj.type, permissions: obj.permissions}
  } else {
    return undefined
  }
}

export const isDisabledProp = (
  obj: PermissionObject,
  path: string[]
): boolean => {
  if (!obj || !path) {
    return false
  }
  if (path.length === 0) {
    return false
  }
  if (path.length > 1) {
    return isDisabledProp((obj as any)[path[0]], path.slice(1))
  }
  if (obj.type === 'board') {
    return isDisabledBoardProp(
      obj.permissions,
      path[0] as keyof BoardPermissions
    )
  }
  if (obj.type === 'task') {
    return isDisabledTaskProp(obj.permissions, path[0] as keyof TaskPermissions)
  }
  return false
}

export const getDefaultForPermissionObject = (
  objectId: DataObjectId,
  type: PermissionObject['type']
): PermissionObject => {
  switch (type) {
    case 'project':
      return {
        type: 'project',
        objectId,
        permissions: getDefaultProjectPermissions()
      }
    case 'board':
      return {
        type: 'board',
        objectId,
        permissions: getDefaultBoardPermissions()
      }
    case 'column':
      return {
        type: 'column',
        objectId,
        permissions: getDefaultColumnPermissions()
      }
    case 'task':
      return {type: 'task', objectId, permissions: getDefaultTaskPermissions()}
    default:
      throw new Error(`Unknown object type ${type}`)
  }
}

export const isAllowedToAdd = (obj: PermissionObject): boolean => {
  if (!obj) {
    return false
  }
  switch (obj.type) {
    case 'project':
      return obj.permissions.addBoard
    case 'board':
      return obj.permissions.addColumn
    case 'column':
      return obj.permissions.addTask
    case 'task':
      return false
    default:
      return false
  }
}

export const showDependsOnTaskType = (
  obj: PermissionObject,
  taskType: TaskPermissionsType
): boolean => {
  if (!obj) {
    return false
  }
  switch (obj.type) {
    case 'project':
      return showDependsOnTaskType(
        {
          type: 'board',
          permissions: obj.permissions.boards,
          objectId: obj.objectId
        },
        taskType
      )
    case 'board':
      return showDependsOnTaskType(
        {
          type: 'column',
          permissions: obj.permissions.columns,
          objectId: obj.objectId
        },
        taskType
      )
    case 'column':
      return obj.permissions[taskType]
        ? obj.permissions.allTasks.show !== obj.permissions[taskType]?.show
        : false
    case 'task':
      return false
    default:
      return false
  }
}
